// @ts-check
import React, { useCallback, useEffect, useMemo, useRef } from "react"
import { TableCell } from "./cells/TableCell.component"
import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual"
import { clsx } from "../../../utils/strings.utils"

/**
 * @param {import("./TableBodyVirtual.component.types").TableBodyVirtualProps} props
 */
export const TableBodyVirtual = (props) => {
  const {
    table,
    tableRef,
    estimateSize,
    enableStickyGroupHeaders,
    getIsRowSticky,
    onContextMenu,
    rowClassName,
    infiniteScroll,
  } = props

  const rows = table.getRowModel().rows
  const isInfinite = Boolean(infiniteScroll)
  const {
    hasNextPage,
    fetchNextPage,
    fetchThreshold = 5,
    isFetchingNextPage,
    loaderText = "Cargando más información...",
  } = infiniteScroll || {}

  const activeStickyIndexRef = useRef(-1)

  const stickyIndexes = useMemo(
    () =>
      enableStickyGroupHeaders ?
        rows
          .map((row, index) => (getIsRowSticky?.(row) ? index : -1))
          .filter((index) => index !== -1)
      : [],
    [rows, enableStickyGroupHeaders, getIsRowSticky]
  )

  useEffect(() => {
    if (!enableStickyGroupHeaders) {
      activeStickyIndexRef.current = -1
    }
  }, [enableStickyGroupHeaders])

  const stickyIndexesSet = useMemo(
    () => new Set(stickyIndexes),
    [stickyIndexes]
  )

  /** @param {number} index */
  const isSticky = (index) => stickyIndexesSet.has(index)

  /** @param {number} index */
  const isActiveSticky = (index) => activeStickyIndexRef.current === index

  const stickyHeaderRangeExtractor = useCallback(
    /** @param {import("@tanstack/react-virtual").Range} range */
    (range) => {
      let found = -1

      for (let i = stickyIndexes.length - 1; i >= 0; i--) {
        if (range.startIndex >= stickyIndexes[i]) {
          found = stickyIndexes[i]
          break
        }
      }

      activeStickyIndexRef.current = found

      const next = new Set([
        activeStickyIndexRef.current,
        ...defaultRangeExtractor(range),
      ])

      return [...next].sort((a, b) => a - b)
    },
    [stickyIndexes]
  )

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableRef.current,
    estimateSize,
    // measure dynamic row height, except in firefox because it measures table border height incorrectly
    measureElement:
      (
        typeof window !== "undefined" &&
        navigator.userAgent.indexOf("Firefox") === -1
      ) ?
        (element) => element?.getBoundingClientRect().height
      : undefined,
    overscan: 10,
    rangeExtractor:
      enableStickyGroupHeaders ? stickyHeaderRangeExtractor : undefined,
  })

  useEffect(() => {
    if (!isInfinite) {
      rowVirtualizer.scrollToIndex(0)
    }
  }, [rows, isInfinite])

  const virtualItems = rowVirtualizer.getVirtualItems()

  useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1]
    const triggerIndex = Math.max(0, rows.length - 1 - fetchThreshold)

    if (
      lastItem &&
      lastItem.index >= triggerIndex &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage?.()
    }
  }, [
    virtualItems,
    rows.length,
    hasNextPage,
    fetchNextPage,
    fetchThreshold,
    isFetchingNextPage,
  ])

  const measureRow = useCallback(
    /** @param {HTMLTableRowElement | null} node */
    (node) => {
      rowVirtualizer.measureElement(node)
    },
    [rowVirtualizer]
  )

  return (
    <tbody
      className="table__body"
      style={{
        height: `${rowVirtualizer.getTotalSize() + (isFetchingNextPage ? 37 : 0)}px`,
      }}
    >
      {virtualItems.map((virtualRow) => {
        const row = rows[virtualRow.index]

        const isGrouped = row.getIsGrouped()

        const className = clsx(
          "table__body-row",
          isGrouped && "table__body-row--grouped",
          isSticky(virtualRow.index) && "table__body-row--sticky",
          isActiveSticky(virtualRow.index) && "table__body-row--sticky-active"
        )

        return (
          <tr
            data-index={virtualRow.index}
            ref={measureRow}
            key={row.id}
            className={clsx(
              className,
              typeof rowClassName === "function" ?
                rowClassName(row)
              : rowClassName
            )}
            onContextMenu={(e) => {
              e.preventDefault()
              onContextMenu?.(e, row)
            }}
            style={
              !isActiveSticky(virtualRow.index) ?
                {
                  position: "absolute",
                  transform: `translateY(${virtualRow.start}px)`,
                }
              : {}
            }
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id} cell={cell} />
            ))}
          </tr>
        )
      })}
      {isInfinite && isFetchingNextPage && (
        <tr
          className="table__body-row table__body-row--loading"
          style={{
            position: "absolute",
            transform: `translateY(${rowVirtualizer.getTotalSize()}px)`,
            width: "100%",
            height: "37px",
          }}
        >
          <td
            className="table__cell table__cell--loading"
            colSpan={table.getAllColumns().length}
          >
            {loaderText}
          </td>
        </tr>
      )}
    </tbody>
  )
}
